import { AttachFileIcon } from "@/components/common/icons/AttachFileIcon/AttachFileIcon"
import {
  ONBOARDING_DEMO_UPLOAD_FLAG,
  ONBOARDING_UPLOAD_STEP_ID,
  useOnboarding,
} from "@/features/onboarding"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"

import { toast } from "@/components/common/Toast"
import { MimeTypeMap } from "@/types/fileUpload"
import { classNames } from "@/utils/classNames"
import { IconProps, IconUpload } from "@tabler/icons-react"
import { UseMutationResult } from "@tanstack/react-query"
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { FileRejection, FileWithPath, useDropzone } from "react-dropzone"
import { Typography } from "../Typography/Typography"

export const OVERLAY_CLASSNAMES =
  "pointer-events-none absolute inset-4 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-primary bg-background/80 backdrop-blur-xs"

export interface FileUploadZoneProps<TData, TError, TContext> {
  mimeTypes: MimeTypeMap
  setIsPending: (isPending: boolean) => void
  setError: (error: TError) => void
  setFiles: (files: TData[]) => void
  onFilesAccepted?: (files: File[]) => boolean
  setUploadedFile?: (file: File | null) => void
  getFileValidationMessage?: (file: FileWithPath) => string | null
  getFileRejectionMessage?: (file: FileWithPath) => string | null
  isDisabled?: boolean
  useUploadMutation: (
    handleSuccess: (data: TData) => void,
    handleError: (error: TError) => void,
    handleSettled: () => void,
  ) => UseMutationResult<TData, TError, FileWithPath, TContext>
  title?: string
  Icon?: React.ComponentType<IconProps>
  children?: React.ReactNode
  idleContent?: React.ReactNode
}

export const FileUploadZone = <TData, TError, TContext>({
  mimeTypes,
  setIsPending,
  setError,
  setFiles,
  onFilesAccepted,
  setUploadedFile,
  getFileValidationMessage,
  getFileRejectionMessage,
  isDisabled = false,
  useUploadMutation,
  title,
  Icon = AttachFileIcon,
  children,
  idleContent,
}: FileUploadZoneProps<TData, TError, TContext>): JSX.Element => {
  const { translate } = useTranslation()
  const {
    handleHighlightedAction,
    getCurrentTour,
    currentStepIndex,
    isActive,
  } = useOnboarding()

  const isOnboardingUploadStep = useMemo(() => {
    if (!isActive) return false
    const currentTour = getCurrentTour()
    const currentStep = currentTour?.steps?.[currentStepIndex]
    return currentStep?.id === ONBOARDING_UPLOAD_STEP_ID
  }, [currentStepIndex, getCurrentTour, isActive])

  // Use refs to track upload counters to avoid stale closures
  const pendingUploadsRef = useRef(0)
  const uploadedFilesRef = useRef<TData[]>([])

  // Use state for UI reactivity
  const [isUploading, setIsUploading] = useState(false)

  const handleSuccess = useCallback(
    (data: TData) => {
      uploadedFilesRef.current = [...uploadedFilesRef.current, data]
      pendingUploadsRef.current--

      // Update parent state with accumulated files
      setFiles(uploadedFilesRef.current)

      // Check if all uploads are complete
      if (pendingUploadsRef.current === 0) {
        setIsPending(false)
        setIsUploading(false)
      }
    },
    [setFiles, setIsPending],
  )

  const handleError = useCallback(
    (error: TError) => {
      pendingUploadsRef.current--
      setError(error)

      // Check if all uploads are complete (including failed ones)
      if (pendingUploadsRef.current === 0) {
        setIsPending(false)
        setIsUploading(false)
      }
    },
    [setError, setIsPending],
  )

  const handleSettled = useCallback(() => {}, [])

  const { mutate } = useUploadMutation(
    handleSuccess,
    handleError,
    handleSettled,
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isUploading) {
        setIsPending(false)
      }
    }
  }, [isUploading, setIsPending])

  const acceptedFileExtensions = useMemo(
    () => ([] as string[]).concat(...Object.values(mimeTypes)),
    [mimeTypes],
  )

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
      // Handle rejections
      if (fileRejections.length > 0) {
        fileRejections.forEach((rejection) => {
          const customMessage = getFileRejectionMessage?.(rejection.file)
          toast.error(
            customMessage ??
              translate("FILE_UPLOAD_ZONE/REJECTED_FILE_TYPE", {
                fileName: rejection.file.name,
                formats: acceptedFileExtensions.join(", "),
              }),
          )
        })
      }

      if (isDisabled) {
        return
      }

      const validatedFiles = acceptedFiles.filter((file) => {
        const validationMessage = getFileValidationMessage?.(file)
        if (validationMessage) {
          toast.error(validationMessage)
          return false
        }
        return true
      })

      // Handle accepted files
      if (validatedFiles.length > 0) {
        // Let parent handle files locally (e.g., Excel preview for QM)
        if (onFilesAccepted?.(validatedFiles)) {
          return
        }

        const shouldSimulateUpload = isOnboardingUploadStep
        if (shouldSimulateUpload) {
          sessionStorage.setItem(ONBOARDING_DEMO_UPLOAD_FLAG, "true")
        }

        // Prevent multiple simultaneous uploads
        if (isUploading) {
          toast.warning(translate("FILE_UPLOAD_ZONE/WAIT_FOR_UPLOAD"))
          return
        }

        // Initialize upload state
        setIsUploading(true)
        pendingUploadsRef.current = validatedFiles.length
        uploadedFilesRef.current = []

        // Clear previous files and set loading state
        setFiles([])
        setIsPending(true)

        // Start uploads
        validatedFiles.forEach((file, index) => {
          try {
            mutate(file)
            // Store the first uploaded file for preview purposes
            if (setUploadedFile && index === 0) {
              setUploadedFile(file)
            }
          } catch {
            pendingUploadsRef.current--

            if (pendingUploadsRef.current === 0) {
              setIsPending(false)
              setIsUploading(false)
            }
          }
        })

        if (shouldSimulateUpload) {
          handleHighlightedAction("file-upload-zone", {
            performNavigation: true,
          })
        }
      }
    },
    [
      mutate,
      setIsPending,
      acceptedFileExtensions,
      setFiles,
      onFilesAccepted,
      setUploadedFile,
      getFileValidationMessage,
      getFileRejectionMessage,
      isUploading,
      isOnboardingUploadStep,
      isDisabled,
      handleHighlightedAction,
    ],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: mimeTypes,
    multiple: true,
    disabled: isUploading || isDisabled, // Prevent drops during upload or when disabled externally
  })

  return (
    <div
      className="h-full w-full"
      aria-labelledby="file-upload-heading"
      data-testid="file-upload-zone"
      role="region"
      aria-label={
        isDragActive
          ? translate("FILE_UPLOAD_ZONE/DROP_FILE_HERE")
          : translate("FILE_UPLOAD_ZONE/DRAG_AND_DROP") +
            " " +
            translate("FILE_UPLOAD_ZONE/UPLOAD_DOCUMENT")
      }
      tabIndex={0}
    >
      <div
        {...getRootProps()}
        className={classNames(
          isDragActive
            ? idleContent
              ? "fixed inset-0 z-50 flex items-center justify-center"
              : "relative h-full"
            : classNames(
                "h-full",
                "relative",
                idleContent
                  ? "cursor-pointer"
                  : classNames(
                      "flex items-center justify-center",
                      "rounded-[40px] border border-dashed border-grey5",
                      "cursor-pointer",
                      "px-S py-S",
                    ),
                (isUploading || isDisabled) && "cursor-not-allowed opacity-60",
              ),
        )}
        role="button"
        aria-disabled={isUploading || isDisabled}
      >
        <input
          {...getInputProps()}
          aria-labelledby="file-input-description"
          data-testid="file-upload-zone-input"
        />
        {isDragActive ? (
          <div id="file-input-description" className={OVERLAY_CLASSNAMES}>
            <IconUpload size={36} className="text-primary" />
            <Typography variant="textS" className="font-medium text-primary">
              {translate("FILE_UPLOAD_ZONE/DROP_FILE_HERE")}
            </Typography>
          </div>
        ) : idleContent ? (
          idleContent
        ) : (
          <div
            id="file-upload-zone"
            className="flex flex-col items-center justify-center text-center"
          >
            {title && (
              <div id="file-upload-heading">
                <Typography as="h1" variant="titleL">
                  {title}
                </Typography>
              </div>
            )}

            <div
              className={classNames(
                "flex items-center justify-center",
                "my-8 h-48 w-48 rounded-full",
                "bg-grey3",
              )}
              aria-hidden="true"
            >
              <Icon size={50} />
            </div>
            <Typography variant="textS">
              {translate("FILE_UPLOAD_ZONE/DRAG_AND_DROP")}{" "}
              <Typography
                as="span"
                variant="textS"
                className="font-bold underline"
              >
                {translate("FILE_UPLOAD_ZONE/UPLOAD_DOCUMENT")}
              </Typography>
            </Typography>
            <br />
            <Typography
              variant="textS"
              className="text-grey5"
              dataTestId={"file-upload-zone-allowed-extensions"}
            >
              {acceptedFileExtensions.join(" / ")}
            </Typography>
          </div>
        )}
        {children}
      </div>
    </div>
  )
}
